"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ZoomExtension = exports.ZoomBehavior = exports.DefaultZoomExtensionParams = void 0;
var tslib_1 = require("tslib");
var class_names_1 = require("../class-names");
var d3 = tslib_1.__importStar(require("../d3-modules"));
var extension_1 = require("../extension");
var scales_1 = require("../scales");
var state_1 = require("../state");
var utils_1 = require("../utils");
/** Default parameter values for `ZoomExtension` */
exports.DefaultZoomExtensionParams = {
  axis: 'none',
  scrollRequireCtrl: false,
  zoomSensitivity: 1,
  panSensitivity: 0.6,
  minZoomedDatapoints: 1
};
/** Behavior class for `ZoomExtension` (allows zooming and panning the heatmap by mouse) */
var ZoomBehavior = /** @class */function (_super) {
  tslib_1.__extends(ZoomBehavior, _super);
  function ZoomBehavior() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    /** Used to merge multiple wheel events into one gesture (needed for correct functioning on Mac touchpad) */
    _this.currentWheelGesture = {
      lastTimestamp: 0,
      lastAbsDelta: 0,
      ctrlKey: false,
      shiftKey: false,
      altKey: false,
      metaKey: false
    };
    /** Used to avoid emitting a new zoom event when adjusting D3 zoom behavior to zoom changes from elsewhere */
    _this.suppressEmit = false;
    return _this;
  }
  Object.defineProperty(ZoomBehavior.prototype, "targetElement", {
    /** DOM element to which the zoom behavior is bound */
    get: function () {
      var _a;
      return (_a = this.state.dom) === null || _a === void 0 ? void 0 : _a.svg;
    },
    enumerable: false,
    configurable: true
  });
  ZoomBehavior.prototype.register = function () {
    var _this = this;
    _super.prototype.register.call(this);
    this.subscribe(this.state.events.render, function () {
      return _this.addZoomBehavior();
    });
    this.subscribe(this.state.events.data, function () {
      _this.adjustZoomExtent();
      _this.adjustZoom();
    });
    this.subscribe(this.state.events.resize, function () {
      _this.adjustZoomExtent();
      _this.adjustZoom();
    });
    this.subscribe(this.state.events.zoom, function (e) {
      if ((e === null || e === void 0 ? void 0 : e.origin) !== exports.ZoomExtension.name) {
        // ignore own events
        _this.adjustZoom();
      }
    });
  };
  ZoomBehavior.prototype.update = function (params) {
    var needsReapply = params.axis !== undefined && params.axis !== this.params.axis;
    _super.prototype.update.call(this, params);
    if (needsReapply) this.addZoomBehavior();
    this.adjustZoomExtent();
    this.adjustZoom();
  };
  /** Initialize zoom behavior (also remove any existing zoom behavior) */
  ZoomBehavior.prototype.addZoomBehavior = function () {
    var _this = this;
    if (!this.targetElement) return;
    if (this.zoomBehavior) {
      // Remove any old behavior
      this.zoomBehavior.on('zoom', null);
      this.targetElement.on('.zoom', null);
      this.targetElement.on('.customzoom', null);
      this.zoomBehavior = undefined;
    }
    if (this.params.axis === 'none') return;
    this.zoomBehavior = d3.zoom();
    this.zoomBehavior.filter(function (e) {
      return e instanceof WheelEvent ? _this.wheelAction(e).kind === 'zoom' : true;
    });
    this.zoomBehavior.wheelDelta(function (e) {
      // Default function is: -e.deltaY * (e.deltaMode === 1 ? 0.05 : e.deltaMode ? 1 : 0.002) * (e.ctrlKey ? 10 : 1)
      var action = _this.wheelAction(e);
      return action.kind === 'zoom' ? _this.params.zoomSensitivity * action.delta : 0;
    });
    this.zoomBehavior.on('zoom', function (e) {
      return _this.handleZoom(e);
    });
    this.targetElement.call(this.zoomBehavior);
    this.targetElement.on('wheel.customzoom', function (e) {
      return _this.handleWheel(e);
    }); // Avoid calling the event 'wheel.zoom', that would conflict with zoom behavior
  };
  /** Handle zoom event coming from the D3 zoom behavior */
  ZoomBehavior.prototype.handleZoom = function (e) {
    var visWorld = this.zoomTransformToVisWorld(e.transform);
    this.state.zoomVisWorldBox(visWorld, exports.ZoomExtension.name, !this.suppressEmit);
    if (e.sourceEvent) {
      this.state.events.hover.next({
        cell: this.state.getPointedCell(e.sourceEvent),
        sourceEvent: e.sourceEvent
      });
    }
  };
  /** Handle event coming directly from the mouse wheel (customizes basic D3 zoom behavior) */
  ZoomBehavior.prototype.handleWheel = function (e) {
    if (!this.targetElement) return;
    e.preventDefault(); // avoid scrolling or previous-page gestures
    // Magic to handle touchpad scrolling on Mac
    this.updateCurrentWheelGesture(e);
    if (this.zoomBehavior) {
      var action = this.wheelAction(e);
      if (action.kind === 'pan') {
        var shiftX = this.params.panSensitivity * (0, scales_1.scaleDistance)(this.state.scales.svgToWorld.x, action.deltaX);
        this.zoomBehavior.duration(1000).translateBy(this.targetElement, shiftX, 0);
      }
      if (action.kind === 'showHelp') {
        this.showScrollingMessage();
      }
    }
    this.state.events.hover.next({
      cell: this.state.getPointedCell(e),
      sourceEvent: e
    });
  };
  /** Magic to handle touchpad scrolling on Mac (when user lifts fingers from touchpad, but the browser is still getting wheel events) */
  ZoomBehavior.prototype.updateCurrentWheelGesture = function (e) {
    var now = Date.now();
    var absDelta = Math.max(Math.abs(e.deltaX), Math.abs(e.deltaY));
    if (now > this.currentWheelGesture.lastTimestamp + 150 || absDelta > this.currentWheelGesture.lastAbsDelta + 1) {
      // Starting a new gesture
      this.currentWheelGesture.ctrlKey = e.ctrlKey;
      this.currentWheelGesture.shiftKey = e.shiftKey;
      this.currentWheelGesture.altKey = e.altKey;
      this.currentWheelGesture.metaKey = e.metaKey;
    }
    this.currentWheelGesture.lastTimestamp = now;
    this.currentWheelGesture.lastAbsDelta = absDelta;
  };
  /** Adjust zoom extent based on current data and canvas size
   * (limit maximum zoom in/out and translation to avoid getting out of the data world) */
  ZoomBehavior.prototype.adjustZoomExtent = function () {
    if (!this.state.dom) return;
    if (!this.zoomBehavior) return;
    this.zoomBehavior.translateExtent([[this.state.boxes.wholeWorld.xmin, -Infinity], [this.state.boxes.wholeWorld.xmax, Infinity]]);
    var canvasWidth = scales_1.Box.width(this.state.boxes.svg);
    var wholeWorldWidth = scales_1.Box.width(this.state.boxes.wholeWorld);
    var minZoom = canvasWidth / wholeWorldWidth; // zoom-out
    var minZoomedDatapoints = Math.max(this.params.minZoomedDatapoints, state_1.MIN_ZOOMED_DATAPOINTS_HARD);
    var maxZoom = Math.max(canvasWidth / minZoomedDatapoints, minZoom); // zoom-in
    this.zoomBehavior.scaleExtent([minZoom, maxZoom]);
    this.zoomBehavior.extent([[this.state.boxes.svg.xmin, this.state.boxes.svg.ymin], [this.state.boxes.svg.xmax, this.state.boxes.svg.ymax]]);
  };
  /** Synchronize the state of the zoom behavior with the visWorld box (e.g. when canvas resizes) */
  ZoomBehavior.prototype.adjustZoom = function () {
    if (!this.targetElement) return;
    if (!this.zoomBehavior) return;
    var currentZoom = this.visWorldToZoomTransform(this.state.boxes.visWorld);
    this.suppressEmit = true;
    this.zoomBehavior.transform(this.targetElement, currentZoom);
    this.suppressEmit = false;
  };
  /** Convert zoom transform to visWorld box */
  ZoomBehavior.prototype.zoomTransformToVisWorld = function (transform) {
    return tslib_1.__assign(tslib_1.__assign({}, this.state.boxes.visWorld), {
      xmin: (this.state.boxes.svg.xmin - transform.x) / transform.k,
      xmax: (this.state.boxes.svg.xmax - transform.x) / transform.k
    });
  };
  /** Convert visWorld box to zoom transform */
  ZoomBehavior.prototype.visWorldToZoomTransform = function (visWorld) {
    var k = (this.state.boxes.svg.xmax - this.state.boxes.svg.xmin) / (visWorld.xmax - visWorld.xmin);
    var x = this.state.boxes.svg.xmin - k * visWorld.xmin;
    var y = 0;
    return new d3.ZoomTransform(k, x, y);
  };
  /** Show overlay with message about how to use scrolling */
  ZoomBehavior.prototype.showScrollingMessage = function () {
    if (!this.state.dom) return;
    if (!this.state.dom.mainDiv.selectAll(".".concat(class_names_1.Class.Overlay)).empty()) return;
    var overlay = (0, utils_1.attrd)(this.state.dom.mainDiv.append('div'), {
      class: class_names_1.Class.Overlay
    });
    (0, utils_1.attrd)(overlay.append('div'), {
      class: class_names_1.Class.OverlayShade
    });
    (0, utils_1.attrd)(overlay.append('div'), {
      class: class_names_1.Class.OverlayMessage
    }).text('Press Ctrl and scroll to apply zoom');
    setTimeout(function () {
      return overlay.remove();
    }, 750);
  };
  /** Categorize wheel event to one of action kinds */
  ZoomBehavior.prototype.wheelAction = function (e) {
    var isHorizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
    var isVertical = Math.abs(e.deltaX) < Math.abs(e.deltaY);
    var modeSpeed = e.deltaMode === 1 ? 25 : e.deltaMode ? 500 : 1; // scroll in lines vs pages vs pixels
    var speedup = this.params.scrollRequireCtrl ? 1 : this.currentWheelGesture.ctrlKey || this.currentWheelGesture.metaKey ? 10 : 1;
    if (isHorizontal) {
      return {
        kind: 'pan',
        deltaX: -e.deltaX * modeSpeed * speedup,
        deltaY: 0
      };
    }
    if (isVertical) {
      if (this.currentWheelGesture.shiftKey) {
        return {
          kind: 'pan',
          deltaX: -e.deltaY * modeSpeed * speedup,
          deltaY: 0
        };
      }
      if (this.params.scrollRequireCtrl && !this.currentWheelGesture.ctrlKey && !this.currentWheelGesture.metaKey) {
        return Math.abs(e.deltaY) * modeSpeed >= 5 ? {
          kind: 'showHelp'
        } : {
          kind: 'ignore'
        };
      }
      return {
        kind: 'zoom',
        delta: -e.deltaY * 0.002 * modeSpeed * speedup
      };
      // Default function for zoom behavior is: -e.deltaY * (e.deltaMode === 1 ? 0.05 : e.deltaMode ? 1 : 0.002) * (e.ctrlKey ? 10 : 1)
    }
    return {
      kind: 'ignore'
    };
  };
  return ZoomBehavior;
}(extension_1.BehaviorBase);
exports.ZoomBehavior = ZoomBehavior;
/** Adds behavior that allows zooming and panning the heatmap by mouse (scroll, drag, double-click)
 * (programmatic zooming via `heatmap.zoom` is independent from this extension) */
exports.ZoomExtension = extension_1.Extension.fromBehaviorClass({
  name: 'builtin.zoom',
  defaultParams: exports.DefaultZoomExtensionParams,
  behavior: ZoomBehavior
});